import { User } from '@/reducers/product-management';
import tracker from '@/tracker';
import { GeoLocation } from '@/api/geo-location';
import { useQuery } from '@tanstack/react-query';
import { callGetApi, callPostApi } from '@/components/api-common';
import PulseRipple from '@/common/components/PulseRipple';
import { useCampaignPulse } from './useCampaignPulseVisible';
import { useEffect } from 'react';
import { isCurrentTimeInInterval } from '@/utils/timer';
import {
  FREE_IPAD_PROMOTION_END_TIME,
  FREE_IPAD_PROMOTION_START_TIME,
} from '@/utils/consts';
import { Icon } from '@arcsite/icons';

const AFFILIATE_LINK = 'https://affiliate.arcsite.com/';
const BOOK_DEMO_LINK = 'https://www.arcsite.com/book-demo-inapp';
const COLLECT_PAYMENT_LINK = 'http://arcsite.com/payments-signup';

const MATCHED_USE_CASES = [
  'Capture Site Data',
  'Perform Material Takeoffs',
  'Build Estimates and Proposals',
];
const MATCHED_INDUSTRY = [
  'Basement Waterproofing',
  'Building Inspection',
  'Decks & Patios',
  'Fencing',
  'Fire Inspection',
  'Foundation Repair',
  'Gutters',
  'Landscape & Irrigation',
  'Pest Control',
  'Site Audits / Inspection',
  'Turf / Synthetic Turf',
  'Architecture',
  'Architectural Engineering',
  'Building Controls',
  'Civil Engineering',
  'Concrete',
  'Electrical / Lighting',
  'Electrical Engineering',
  'Energy & Solar',
  'Engineering & Mechanical',
  'Flooring',
  'HVAC',
  'Landscape Designer',
  'Maintenance',
  'Mechanical/Mechanical Engineering',
  'Plumbing',
  'Roofing',
  'Security / Security Inspection',
  'Structural Engineering',
  'Sub Contractor',
  'Telecom',
];

export const USER_CAMPAIGN_MAP = {
  BookDemo: {
    key: 'BookDemo',
    text: 'Book Demo',
    link: BOOK_DEMO_LINK,
    icon: <Icon name="book-demo-duotone-24x24" />,
  },
  BecomeAnAffiliate: {
    key: 'BecomeAnAffiliate',
    text: 'Become an Affiliate',
    link: AFFILIATE_LINK,
    icon: <Icon name="gift-duotone-24x24" />,
  },
  CollectPayment: {
    key: 'CollectPayment',
    text: 'Collect Payments',
    link: COLLECT_PAYMENT_LINK,
    icon: <Icon name="payment-card-duotone-2-24x24" />,
  },
  FreeIpad: {
    key: 'FreeIpad',
    text: 'See Our iPad Offer',
    link: 'https://www.arcsite.com/promo/holiday25?utm_medium=Lifecycle%2FEmail&utm_source=Promo&utm_campaign=Dec-25&utm_content=App-Banner',
    icon: <Icon name="money-bag-duotone-24x24" />,
  },
} as const;

export default function ArcSiteCampaign({ user }: { user: User }) {
  const campaign = useArcSiteCampaign(user);
  const { shouldShowPulse, handleVisit, handleCampaignClick } =
    useCampaignPulse();
  useEffect(() => {
    if (campaign?.key === 'CollectPayment') {
      handleVisit();
    }
  }, [campaign?.key, handleVisit]);
  if (!campaign) return null;
  const { key, text, icon, link } = campaign;
  return (
    <a
      className="relative mr-4 hidden sm:block"
      href={link}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        if (key === 'BookDemo') {
          tracker.logEvent('web_book_demo_button_click');
        } else if (key === 'CollectPayment') {
          handleCampaignClick();
          tracker.logEvent('tap_request_arcsite_payments', {
            Source: 'web',
          });
        } else if (key === 'FreeIpad') {
          tracker.logEvent('web_promotion_click');
        } else {
          tracker.logEvent('web_become_an_affiliate_button_click');
        }
      }}
    >
      <div className="bg-[#E3571C] text-xs px-2.5 py-1 text-white rounded font-semibold flex items-center">
        {icon}
        <span className="whitespace-nowrap ml-2 hidden sm:block">{text}</span>
      </div>
      {key === 'CollectPayment' && shouldShowPulse && (
        <PulseRipple className="absolute -top-2 -right-2" />
      )}
    </a>
  );
}

const useArcSiteCampaign = (user: User) => {
  const freeIpadMatched =
    user.default_org_purchase_type == 'TRIAL' &&
    isCurrentTimeInInterval(
      FREE_IPAD_PROMOTION_START_TIME,
      FREE_IPAD_PROMOTION_END_TIME
    );
  const bookDemoTierMatched =
    user.default_org_purchase_type == 'TRIAL' ||
    user.default_org_user_level === 'BASIC' ||
    user.default_org_user_level === 'PRO';

  const collectPaymentTierMatched =
    user.default_org_user_level == 'PREMIUM_PLUS' &&
    !user.expired &&
    user.default_org_purchase_type !== 'TRIAL';

  const { data: geoLocation, isLoading: isGeoLocationLoading } = useQuery({
    queryKey: ['geo-location'],
    queryFn: () => callGetApi<GeoLocation>('user/service/geo_location/'),
    enabled: collectPaymentTierMatched,
  });
  const { data: paymentSettings, isLoading: isPaymentSettingsLoading } =
    useQuery({
      queryKey: ['payment_settings'],
      queryFn: () =>
        callPostApi<{ enabled: boolean }>(
          '/user/service/payments/payment_settings/'
        ),
      enabled: collectPaymentTierMatched,
    });
  const isUS = geoLocation?.country_code === 'US';
  const isPaymentEnabled = paymentSettings?.enabled;

  const useCaseMatched = MATCHED_USE_CASES.some(useCase =>
    user.use_cases.includes(useCase)
  );
  const industryMatched = MATCHED_INDUSTRY.includes(user.industry || '');

  if (freeIpadMatched) {
    return USER_CAMPAIGN_MAP.FreeIpad;
  }

  if (bookDemoTierMatched && (useCaseMatched || industryMatched)) {
    return USER_CAMPAIGN_MAP.BookDemo;
  }

  if (isGeoLocationLoading || isPaymentSettingsLoading) {
    return null;
  }

  if (collectPaymentTierMatched && isUS && !isPaymentEnabled) {
    return USER_CAMPAIGN_MAP.CollectPayment;
  }

  return USER_CAMPAIGN_MAP.BecomeAnAffiliate;
};
